import { DocumentSnapshot, QueryDocumentSnapshot } from 'firebase/firestore'
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  firestore,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from '~providers/firebase'
import { ChatSection, ChatTemplate, CreateChatType, ExistingChat, IChat } from '~types'

export const initialChatSection: ChatSection = {
  image: { src: '' },
  audio: {
    teacher: { src: '' },
    student: { src: '' },
  },
}

const chatFromSnapshot = (snapshot: QueryDocumentSnapshot | DocumentSnapshot): ExistingChat => {
  const {
    ref: { path },
    id,
  } = snapshot
  const {
    name,
    level,
    layout,
    sections,
    created_at,
    updated_at,
    author_id,
    author_name,
    teacher_id,
    template_id,
    parent_class_id,
  } = snapshot.data()

  const currentSections = sections || {}
  const newSections: Record<number, ChatSection> = {
    ...Array.from(Array(layout.rows * layout.columns).keys()).map((index) => ({
      ...initialChatSection,
      audio: {
        teacher: {
          src: currentSections[index]?.audio?.teacher?.src || '',
        },
        student: {
          src: currentSections[index]?.audio?.student?.src || '',
        },
      },
      image: {
        src: currentSections[index]?.image?.src || '',
        scale: currentSections[index]?.image?.scale,
        translateX: currentSections[index]?.image?.translateX,
        translateY: currentSections[index]?.image?.translateY,
      },
      ...(currentSections[index]?.order !== undefined && { order: currentSections[index].order }),
    })),
  }

  // Allow sharing of chats and group chats
  const regex = /^classes\/(?<classId>[\da-zA-Z]+)\/chats\/(?<chatId>[\da-zA-Z]+)$/
  const matches = path.match(regex)

  const classId = matches ? matches.groups?.classId : parent_class_id

  if (!classId) {
    throw new Error(`classId not found for ${path}`)
  }

  return {
    path,
    id,
    name,
    level,
    layout,
    sections: newSections,
    created_at: (created_at ?? Timestamp.now()).toDate().toISOString(),
    updated_at: (updated_at ?? Timestamp.now()).toDate().toISOString(),
    author_id,
    author_name: author_name || 'Unknown',
    teacher_id,
    template_id,
    classId,
    parent_class_id,
  }
}

const listChats = (path: string) =>
  query(collection(firestore, path, 'chats'), orderBy('updated_at', 'desc'))

const createChat = (
  parent: string, // Parent document path
  chat: CreateChatType,
): Promise<ExistingChat> => {
  const regex = /^classes\/(?<classId>[\da-zA-Z]+)$/
  const matches = parent.match(regex)

  const classId = matches ? matches.groups?.classId : undefined

  if (!classId) {
    throw new Error(`classId not found for ${parent}`)
  }

  const currentSections = chat.sections || {}
  const sections: Record<number, ChatSection> = {
    ...Array.from(Array(chat.layout.rows * chat.layout.columns).keys()).map((index) => ({
      ...initialChatSection,
      audio: {
        teacher: {
          src: currentSections[index]?.audio?.teacher?.src || '',
        },
        student: {
          src: currentSections[index]?.audio?.student?.src || '',
        },
      },
      image: {
        src: currentSections[index]?.image?.src || '',
        ...(currentSections[index]?.image?.scale !== undefined && {
          scale: currentSections[index].image.scale,
        }),
        ...(currentSections[index]?.image?.translateX !== undefined && {
          translateX: currentSections[index].image.translateX,
        }),
        ...(currentSections[index]?.image?.translateY !== undefined && {
          translateY: currentSections[index].image.translateY,
        }),
      },
      ...(currentSections[index]?.order !== undefined && { order: currentSections[index].order }),
    })),
  }

  const values = {
    ...chat,
    sections,
  }

  if (values.template_id === undefined) {
    delete values.template_id
  }

  return addDoc(collection(firestore, parent, 'chats'), {
    ...values,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  }).then((doc) => ({
    ...values,
    path: doc.path,
    id: doc.id,
    classId,
    created_at: Timestamp.now().toDate().toISOString(),
    updated_at: Timestamp.now().toDate().toISOString(),
  }))
}

const updateChat = (path: string, updates: Partial<Omit<IChat, 'id' | 'path'>>): Promise<void> => {
  if (!path) {
    throw new Error('Path is not set')
  }

  return updateDoc(doc(firestore, path), {
    ...updates,
    updated_at: serverTimestamp(),
  })
}

const deleteChat = (path: string): Promise<void> => {
  if (!path) {
    throw new Error('Path is not set')
  }

  return deleteDoc(doc(firestore, path))
}

const duplicateChat = async (
  authorId: string,
  authorName: string,
  classId: string,
  chat: Required<ChatTemplate>,
): Promise<ExistingChat> => {
  if (!authorId || !classId || !chat) {
    throw new Error('Invalid arguments')
  }

  const path = `users/${authorId}/chats/${chat.id}`

  const required = (({ id, name, layout, sections, author_id }) => ({
    id,
    name,
    layout,
    sections: {
      ...Object.entries(sections).map(([, section]) => ({
        ...initialChatSection,
        audio: {
          teacher: {
            src: section?.audio?.teacher?.src || '',
          },
          student: {
            src: section?.audio?.student?.src || '',
          },
        },
        image: {
          src: section?.image?.src || '',
          ...(section?.image?.scale !== undefined && { scale: section.image.scale }),
          ...(section?.image?.translateX !== undefined && {
            translateX: section.image.translateX,
          }),
          ...(section?.image?.translateY !== undefined && {
            translateY: section.image.translateY,
          }),
        },
        ...(section?.order !== undefined && { order: section.order }),
      })),
    },
    teacher_id: author_id,
  }))(chat)

  const values = {
    ...required,
    level: '',
    parent_chat_id: chat.id,
    parent_class_id: classId,
    author_id: authorId,
    author_name: authorName,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  }

  await setDoc(doc(firestore, path), values)

  return {
    ...values,
    path,
    id: chat.id,
    created_at: Timestamp.now().toDate().toISOString(),
    updated_at: Timestamp.now().toDate().toISOString(),
    classId,
  }
}

const listChatReplies = (classId: string, chatId: string) => {
  if (!classId || !chatId) {
    return null
  }

  return query(
    collectionGroup(firestore, 'chats'),
    where('parent_chat_id', '==', chatId),
    where('parent_class_id', '==', classId),
    orderBy('updated_at', 'desc'),
  )
}

export default {
  listChats,
  createChat,
  updateChat,
  deleteChat,
  chatFromSnapshot,
  duplicateChat,
  listChatReplies,
}
